import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import HotelRoundedIcon from "@mui/icons-material/HotelRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import AppButton from "../../components/button/AppButton";
import StatCard from "../../components/cards/StatCard";
import AppTextInput from "../../components/inputs/AppTextInput";
import AppSelectInput from "../../components/inputs/AppSelectInput";
import CreateAccountModal from "../../components/modals/CreateAccountModal";
import AccountDetailsModal from "../../components/modals/AccountDetailsModal";
import UpdateSubscriptionModal from "../../components/modals/UpdateSubscriptionModal";
import CreateCleanerModal from "../../components/modals/CreateCleanerModal";
import UpsertPropertyModal from "../../components/modals/UpsertPropertyModal";
import UpsertUnitModal from "../../components/modals/UpsertUnitModal";
import { formatDate } from "../../utils/helperFunctions/dateFormatter";
import { readableText } from "../../utils/helperFunctions/stringFormatter";
import { useAccountsScreen } from "./hooks/useAccountsScreen";

const statusColor = (status: string) => {
  if (status === "active") {
    return "success";
  }

  if (status === "trial") {
    return "warning";
  }

  if (status === "expired" || status === "cancelled") {
    return "error";
  }

  return "default";
};

const AccountsScreen = () => {
  const {
    accounts,
    selectedAccount,
    subscriptions,
    subscriptionFilterOptions,
    pagination,
    filters,
    loading,
    subscriptionsLoading,
    detailsLoading,
    saving,
    error,
    stats,
    createAccountOpen,
    setCreateAccountOpen,
    detailsOpen,
    closeDetails,
    subscriptionOpen,
    setSubscriptionOpen,
    cleanerOpen,
    setCleanerOpen,
    updateFilter,
    refreshAccounts,
    openAccountDetails,
    handleCreateAccount,
    handleUpdateSubscription,
    handleCreateCleaner,
    handleToggleUserStatus,
    handleDeleteUser,
    handleDeleteAccount,
    propertyOpen,
    setPropertyOpen,
    selectedProperty,

    unitOpen,
    setUnitOpen,
    selectedUnit,
    initialUnitPropertyId,

    openCreateProperty,
    openEditProperty,
    handleSaveProperty,

    openCreateUnit,
    openEditUnit,
    handleSaveUnit,
  } = useAccountsScreen();

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            Accounts
          </Typography>
          <Typography color="text.secondary">
            Manage owners, cleaners, trials and paid subscriptions.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <AppButton
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            onClick={refreshAccounts}
            disabled={loading || subscriptionsLoading}
          >
            Refresh
          </AppButton>

          <AppButton
            startIcon={<AddRoundedIcon />}
            onClick={() => setCreateAccountOpen(true)}
            disabled={subscriptionsLoading || subscriptions.length === 0}
          >
            Create account
          </AppButton>
        </Stack>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: 2,
        }}
      >
        <StatCard
          title="Total accounts"
          value={stats.total}
          icon={<PeopleRoundedIcon color="primary" />}
        />
        <StatCard
          title="Paid active"
          value={stats.active}
          icon={<PaidRoundedIcon color="success" />}
        />
        <StatCard
          title="Trial"
          value={stats.trial}
          icon={<AccessTimeRoundedIcon color="warning" />}
        />
        <StatCard
          title="Expired/cancelled"
          value={stats.expired}
          icon={<HotelRoundedIcon color="error" />}
        />
      </Box>

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "2fr 1fr 1fr",
                },
                gap: 2,
              }}
            >
              <AppTextInput
                label="Search"
                value={filters.search || ""}
                onChange={(value) => updateFilter("search", value)}
                placeholder="Search by account, owner or email"
              />

              <AppSelectInput
                label="Status"
                value={filters.status || ""}
                onChange={(value) => updateFilter("status", value)}
                options={[
                  { label: "All statuses", value: "" },
                  { label: "Trial", value: "trial" },
                  { label: "Paid", value: "active" },
                  { label: "Expired", value: "expired" },
                  { label: "Cancelled", value: "cancelled" },
                ]}
              />

              <AppSelectInput
                label="Subscription"
                value={filters.plan || ""}
                onChange={(value) => updateFilter("plan", value)}
                disabled={subscriptionsLoading}
                options={subscriptionFilterOptions}
              />
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Account</TableCell>
                      <TableCell>Subscription</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Valid until</TableCell>
                      <TableCell>Users</TableCell>
                      <TableCell>Units</TableCell>
                      <TableCell>Bookings</TableCell>
                      <TableCell align="right">Options</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {accounts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <Typography
                            color="text.secondary"
                            sx={{ textAlign: "center" }}
                          >
                            No accounts found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      accounts.map((account) => (
                        <TableRow key={account.id} hover>
                          <TableCell>
                            <Stack spacing={0.3}>
                              <Typography sx={{ fontWeight: 900 }}>
                                {account.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {account.readable_subscription_status || "-"}
                              </Typography>
                            </Stack>
                          </TableCell>

                          <TableCell>
                            {account.subscription_name ||
                              readableText(account.subscription_plan)}
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={readableText(account.subscription_status)}
                              color={
                                statusColor(account.subscription_status) as any
                              }
                              size="small"
                              sx={{ fontWeight: 800 }}
                            />
                          </TableCell>

                          <TableCell>
                            {formatDate(account.subscription_valid_until)}
                          </TableCell>

                          <TableCell>{account.users_count ?? "-"}</TableCell>
                          <TableCell>
                            {account.properties_count ?? "-"}
                          </TableCell>
                          <TableCell>{account.bookings_count ?? "-"}</TableCell>

                          <TableCell align="right">
                            <Tooltip title="View account">
                              <span>
                                <AppButton
                                  variant="outlined"
                                  onClick={() => openAccountDetails(account.id)}
                                  disabled={detailsLoading || saving}
                                >
                                  View
                                </AppButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}

            {pagination ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {pagination.from || 0}–{pagination.to || 0} of{" "}
                  {pagination.total}
                </Typography>

                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    alignItems: "center",
                  }}
                >
                  <Select
                    size="small"
                    value={String(filters.per_page || 20)}
                    onChange={(event) =>
                      updateFilter("per_page", Number(event.target.value))
                    }
                  >
                    <MenuItem value="10">10</MenuItem>
                    <MenuItem value="20">20</MenuItem>
                    <MenuItem value="50">50</MenuItem>
                  </Select>

                  <Pagination
                    page={pagination.current_page}
                    count={pagination.last_page}
                    color="primary"
                    onChange={(_, page) => updateFilter("page", page)}
                  />
                </Stack>
              </Box>
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      <CreateAccountModal
        open={createAccountOpen}
        subscriptions={subscriptions}
        onClose={() => setCreateAccountOpen(false)}
        onSubmit={handleCreateAccount}
      />

      <AccountDetailsModal
        open={detailsOpen}
        account={selectedAccount}
        onClose={closeDetails}
        onOpenSubscription={() => setSubscriptionOpen(true)}
        onOpenCleaner={() => setCleanerOpen(true)}
        onAddProperty={openCreateProperty}
        onEditProperty={openEditProperty}
        onAddUnit={openCreateUnit}
        onEditUnit={openEditUnit}
        onToggleUserStatus={handleToggleUserStatus}
        onDeleteUser={handleDeleteUser}
        onDeleteAccount={handleDeleteAccount}
      />

      <UpdateSubscriptionModal
        open={subscriptionOpen}
        account={selectedAccount}
        subscriptions={subscriptions}
        onClose={() => setSubscriptionOpen(false)}
        onSubmit={handleUpdateSubscription}
      />

      <CreateCleanerModal
        open={cleanerOpen}
        onClose={() => setCleanerOpen(false)}
        onSubmit={handleCreateCleaner}
      />
      <UpsertPropertyModal
        open={propertyOpen}
        property={selectedProperty}
        onClose={() => setPropertyOpen(false)}
        onSubmit={handleSaveProperty}
      />

      <UpsertUnitModal
        open={unitOpen}
        account={selectedAccount}
        unit={selectedUnit}
        initialPropertyId={initialUnitPropertyId}
        onClose={() => setUnitOpen(false)}
        onSubmit={handleSaveUnit}
      />
    </Stack>
  );
};

export default AccountsScreen;
